/**
 * Party-facts WP-6b / spec lever **E2** + lever **I** UI — **the "why?" popover.**
 *
 * One affordance, four mount sites (the `parties` field's chips, the panel's catch-all chips, Linked
 * Records' evidence line, and the fact-edit modal), and ONE component — because the question is the
 * same everywhere: *why does the system say this party has this role on this deal?*
 *
 * **Everything it renders was decided by the server** (`crm/fact_evidence.py`): the cited emails are
 * the rows the deal's own audited evidence read returns to THIS viewer, `from → to` are gated entity
 * names with the rest as a count, the chain is the ledger's, the footer's number is lever F's own
 * count. This component adds no rule; its decisions are which section to show and what to call it.
 *
 * ⛔ **NOTHING IS FETCHED UNTIL IT OPENS.** `useCrmFactEvidence` is passed `enabled: open`, so a panel
 * with twenty chips issues zero requests and writes zero audit rows until someone asks a question.
 * The read is audited at the moment of intent, which is the whole point of moving it behind a click
 * (the same discipline the F.5 body door follows).
 *
 * **Opening an email opens the TIMELINE, in place.** A citation row swaps the body of the popover for
 * `CrmEvidenceTimeline` anchored at that `event_log_id`, rather than trying to drive a timeline that
 * may not be on screen — Linked Records lives on an entity profile, where there is no record panel
 * underneath. The `list_id` / `entry_id` come off the trace, so the popover needs no context.
 *
 * **The footer is lever F's.** *"All N messages with this party"* calls `showOnly(node_id)` on the
 * party-filter context with the node id the server sent, untouched.
 *
 * ⛔ **AND IT NEEDS TWO SIGNALS, NOT ONE** (adversarial review 2026-09-05, F2). `party.available`
 * only says a provider is mounted, and `CrmRecordPanelContainer` mounts one UNCONDITIONALLY — so on
 * its own it is true in exactly the rollout window this feature contemplates
 * (`crm_fact_evidence_trace_enabled` ON, `crm_timeline_party_filter_enabled` still OFF) and the
 * operator gets a live button whose click sends a `party` the server ignores. `party.count` is the
 * SERVER's own answer: `null` means lever F is dark, because the reader takes the number off the
 * `party_filter` block, which rides only a flag-ON payload. Both must hold.
 */
import { useEffect, useRef, useState } from 'react';

import { useCrmFactEvidence, useWriteCrmFactNote } from '../../hooks/useCrm';
import type { CrmFactCitation, CrmFactEvidenceResponse, CrmFactNote } from '../../types';
import { CrmEvidenceTimeline } from './CrmEvidenceTimeline';
import { formatRole } from './crmEntityRoles';
import { fmtShortDate } from './crmFormat';
import { useCrmPartyFilter } from './crmPartyFilter';
import styles from './CrmFactEvidencePopover.module.css';

/** `crm_relationship_fact_note.text` is `Str(200)` and the server REFUSES rather than truncates, so
 *  the input caps at the same number — a person's sentence must not be cut in half by either side. */
export const NOTE_MAX = 200;

function fmtWhen(iso: string | null): string {
  return iso ? fmtShortDate(iso) : '—';
}

/** `from → to`, as GATED NAMES plus a count. Never an address — the server sends none. */
function envelope(cite: CrmFactCitation): string {
  const from = cite.from?.name ?? (cite.from_hidden ? 'someone not shown' : null);
  const named = cite.to.map((r) => r.name ?? '(unnamed)');
  // ONE trailing phrase rather than a per-name placeholder run: "+2 not shown" says exactly what is
  // true — there were more recipients and this viewer may not be told who — while a list of three
  // "(withheld)" chips reads as though the names were nearly there.
  if (cite.to_hidden > 0) named.push(`+${cite.to_hidden} not shown`);
  if (!from && named.length === 0) return '';
  return `${from ?? '—'} → ${named.length > 0 ? named.join(', ') : '—'}`;
}

/** `stated · cc · inbound` — the per-EMAIL facets X-4 moved off the fact and onto the citation. */
function facets(cite: CrmFactCitation): string {
  return [cite.relevance, cite.appearance, cite.direction]
    .filter((v): v is string => !!v)
    .map(formatRole)
    .join(' · ');
}

function NoteLine({ note, label }: { note: CrmFactNote; label: string }) {
  return (
    <p className={styles.note}>
      <span className={styles.noteWho}>
        {label}
        {note.created_at ? `, ${fmtShortDate(note.created_at)}` : ''}
        {/* Rule 3 — computed at read time, never stored. The whole sentence is the accessible name:
            a bare word announces nothing useful out of context. */}
        {note.stale && (
          <span className={styles.stale}
                title="Written before the role or its evidence changed"
                aria-label="Written before the role or its evidence changed">stale</span>
        )}
      </span>
      <br />
      {note.text}
    </p>
  );
}

function Notes({ trace, assertionId }: { trace: CrmFactEvidenceResponse; assertionId: number }) {
  const [draft, setDraft] = useState('');
  const write = useWriteCrmFactNote(assertionId);
  const human = trace.notes.human;
  const machine = trace.notes.machine;
  return (
    <div className={styles.section}>
      <div className={styles.sectionLabel}>Notes</div>
      {/* Lever I rule 1 — HUMAN FIRST, then the model, each labelled and dated. The order is the
          rule, not a layout preference: a person's sentence is not a variant of the model's. */}
      {human && <NoteLine note={human} label="A colleague" />}
      {machine && (
        <NoteLine note={machine}
                  label={machine.producer_kind ? `AI (${formatRole(machine.producer_kind)})` : 'AI'} />
      )}
      {!human && !machine && <p className={styles.tertiary}>No note yet.</p>}
      <div className={styles.noteForm}>
        <input className={styles.noteInput} maxLength={NOTE_MAX} value={draft}
               aria-label="Your note on this role"
               placeholder={human ? 'Replace your note…' : 'Add a note (25 words)'}
               disabled={write.isPending}
               onChange={(e) => setDraft(e.target.value)}
               onKeyDown={(e) => {
                 if (e.key === 'Enter' && draft.trim()) {
                   write.mutate({ text: draft.trim() }, { onSuccess: () => setDraft('') });
                 }
               }} />
        <button type="button" className={styles.noteBtn}
                disabled={write.isPending || !draft.trim()}
                onClick={() => write.mutate({ text: draft.trim() },
                                            { onSuccess: () => setDraft('') })}>
          {write.isPending ? 'Saving…' : 'Save'}
        </button>
        {/* A human may withdraw their OWN note and nothing else (rule 1). Offered only when there is
            one, so the control cannot suggest an act on the model's row. */}
        {human && !write.isPending && (
          <button type="button" className={styles.noteBtn}
                  onClick={() => write.mutate({ text: '', withdraw: true })}>Remove</button>
        )}
      </div>
      {/* Explicit, inline, and the field stays usable — never a silent failure on a governed write. */}
      {write.isError && (
        <div className={styles.error} role="alert">Couldn’t save that note.</div>
      )}
    </div>
  );
}

function Chain({ trace }: { trace: CrmFactEvidenceResponse }) {
  const { chain } = trace;
  const lines: string[] = [];
  if (chain.supersedes?.assertion_id != null) {
    lines.push(`Replaced an earlier record (revision ${chain.supersedes.revision ?? '?'}).`);
  }
  if (chain.retired_by) {
    lines.push(chain.retired_by.assertion_id != null
      ? 'Superseded by a later record.'
      : `Withdrawn${chain.retired_by.reason ? ` — ${chain.retired_by.reason}` : ''}.`);
  }
  if (chain.decided_via) {
    // A decider the entity gate withheld renders as "a colleague" — the RULING is reported, the
    // party is not named. Same sentence `CrmLinkedRecords.evidenceLine` uses, for the same reason.
    lines.push(`${formatRole(chain.decided_via)} by ${chain.decided_by?.name ?? 'a colleague'}`
      + `${chain.decided_at ? ` on ${fmtShortDate(chain.decided_at)}` : ''}.`);
  }
  if (lines.length === 0) return null;
  return (
    <div className={styles.section}>
      <div className={styles.sectionLabel}>Record</div>
      {lines.map((line, i) => <div key={i} className={styles.muted}>{line}</div>)}
    </div>
  );
}

function Body({ trace, assertionId }: { trace: CrmFactEvidenceResponse; assertionId: number }) {
  const [anchor, setAnchor] = useState<number | null>(null);
  const party = useCrmPartyFilter();
  // F12 — the drill-down is per RECORD. `FactWhyButton` can outlive a ‹ › hop (the panel keeps the
  // field mounted), and an `event_log_id` from deal A anchored inside deal B's timeline would mark
  // nothing and scroll nowhere. Reset DURING render, the pattern `crmPartyFilter` and
  // `CrmRecordTimeline` already use for exactly this hazard — an effect is one render too late.
  const [host, setHost] = useState(trace.entry_id);
  if (host !== trace.entry_id) { setHost(trace.entry_id); setAnchor(null); }

  if (anchor != null && host === trace.entry_id) {
    return (
      <>
        <button type="button" className={styles.back} onClick={() => setAnchor(null)}>
          ‹ back to the evidence for this role
        </button>
        <CrmEvidenceTimeline listId={trace.list_id} entryId={trace.entry_id} anchor={anchor} />
      </>
    );
  }

  const count = trace.party.count;
  const name = trace.party.name ?? 'this party';
  return (
    <>
      {trace.citations.length === 0 ? (
        // Lever E2's last line — a transcription has no email behind it and says so, rather than
        // rendering an empty list that reads as "the evidence failed to load".
        <p className={styles.muted}>
          {trace.producer_kind === 'manual'
            ? 'Recorded by a colleague'
            : `Recorded by ${formatRole(trace.producer_kind)}`}
          {trace.observed_at ? ` on ${fmtWhen(trace.observed_at)}` : ''}, with no email behind it.
        </p>
      ) : (
        <ul className={styles.cites}>
          {trace.citations.map((cite) => (
            <li key={cite.event_log_id}>
              <button type="button" className={styles.cite}
                      data-cite={cite.event_log_id}
                      onClick={() => setAnchor(cite.event_log_id)}>
                <span className={styles.citeSubject}>{cite.subject || '(no subject)'}</span>
                <span className={styles.citeMeta}>
                  {[fmtWhen(cite.occurred_at), facets(cite)].filter(Boolean).join(' · ')}
                </span>
                <span className={styles.citeMeta}>{envelope(cite)}</span>
                {/* Lever I rule 5 — the citation's template note is read ONLY here, inside the
                    popover. Never on a chip, never in a cached cell. */}
                {cite.note && <span className={styles.citeNote}>{cite.note}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {trace.acting_for && (
        <div className={styles.muted}>Acting for <strong>{trace.acting_for.name}</strong>.</div>
      )}
      <Chain trace={trace} />
      <Notes trace={trace} assertionId={assertionId} />
      <div className={styles.footer}>
        {/* F2 — a provider AND the server's own flag signal. `count == null` is lever F dark, and a
            button that cannot filter must not be painted as one. */}
        {party.available && count != null ? (
          <button type="button" className={styles.link} data-show-all={trace.party.node_id}
                  onClick={() => party.showOnly(trace.party.node_id)}>
            {`All ${count} messages with ${name}`}
          </button>
        ) : count != null ? (
          <span className={styles.tertiary}>
            {`${count} message${count === 1 ? '' : 's'} with ${name} on this deal`}
          </span>
        ) : null}
      </div>
    </>
  );
}

/** The panel itself. Exported for tests and for a caller that owns its own open state. */
export function CrmFactEvidencePopover(
  { assertionIds, label, onClose }:
  { assertionIds: number[]; label: string; onClose: () => void },
) {
  // A chip can stand on several live facts (an entity that is both a participant and an adviser).
  // The trace addresses ONE, so the popover switches rather than merging: two facts are two claims
  // with two evidence sets, and stacking them would attribute one's emails to the other.
  const [which, setWhich] = useState(0);
  const assertionId = assertionIds[which] ?? assertionIds[0] ?? null;
  const q = useCrmFactEvidence(assertionId, true);
  const cardRef = useRef<HTMLDivElement>(null);

  // Focus moves INTO the panel on open, so Tab walks its own controls and its Escape handler can
  // fire — the `CrmFactEditModal` pattern, and for the same reason: without it focus stays on the
  // trigger behind the panel and the surrounding surface swallows the key.
  useEffect(() => { cardRef.current?.focus(); }, []);

  return (
    <div ref={cardRef} className={styles.panel} role="dialog" aria-modal="false"
         aria-label={`Why ${label}?`} tabIndex={-1}
         // ⚠ The panel opens INSIDE surfaces that close on an outside mousedown — the fact-edit
         // modal's backdrop is one — so reading the evidence must not dismiss the thing being read.
         // It deliberately does NOT close on an outside click of its own: a document-level listener
         // would fight the host's, and Escape plus the × plus re-clicking the trigger are three ways
         // out already. `aria-modal="false"` because it is a popover, not a modal: focus moves in,
         // but the panel behind it stays reachable.
         onMouseDown={(e) => e.stopPropagation()}
         onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }}>
      <div className={styles.head}>
        <span className={styles.title}>Why {label}?</span>
        <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>×</button>
      </div>
      {assertionIds.length > 1 && (
        <div className={styles.switcher} role="group" aria-label="Which record">
          {assertionIds.map((id, i) => (
            <button key={id} type="button" aria-pressed={i === which}
                    className={i === which ? `${styles.switch} ${styles.switchOn}` : styles.switch}
                    onClick={() => setWhich(i)}>{`Record ${i + 1}`}</button>
          ))}
        </div>
      )}
      {q.isLoading && <div className={styles.tertiary}>Loading…</div>}
      {/* Existence-safe by design: the endpoint 404s for a fact this caller may not be told about
          AND while the flag is dark, so an error here is "nothing to show", not a failure banner. */}
      {!q.isLoading && !q.data && (
        <div className={styles.tertiary}>No evidence trace is available for this record.</div>
      )}
      {q.data && <Body trace={q.data} assertionId={assertionId as number} />}
    </div>
  );
}

/**
 * The "why?" trigger and its popover, as ONE component — so every mount site is a single line and
 * none of them owns the open state, the fetch gating or the outside-click rule.
 *
 * ⚠ Renders the DISABLED placeholder PR-D2 shipped when there is no fact id to ask about (an
 * un-hydrated chip, a payload that predates the flag). Disabled rather than absent because an
 * affordance that appears later moves everything under it.
 */
export function FactWhyButton(
  { assertionIds, label }: { assertionIds: readonly number[]; label: string },
) {
  const [open, setOpen] = useState(false);
  const ids = assertionIds.filter((n) => Number.isFinite(n));
  if (ids.length === 0) {
    return (
      <button type="button" className={styles.triggerOff} disabled
              title="why? — no record behind this yet">why?</button>
    );
  }
  return (
    <span className={styles.host}
          // The panel sits inside a row that is often itself clickable (a Linked Records row opens
          // the record; a chip row opens the modal). Reading the evidence is not asking to navigate
          // away from it, so the click stops here.
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}>
      <button type="button" className={styles.trigger} aria-expanded={open}
              title={`Why ${label}?`} onClick={() => setOpen((v) => !v)}>why?</button>
      {open && (
        <CrmFactEvidencePopover assertionIds={[...ids]} label={label}
                                onClose={() => setOpen(false)} />
      )}
    </span>
  );
}

export default FactWhyButton;
